import { z } from "zod";

// ---------------------------------------------------------------------------
// Business insights
//
// Persisted, business-specific insights generated server-side (deterministic
// rules + AI generators). Stored at:
//   Businesses/{businessId}/insights/{insightId}   (insightId === dedupeKey)
//
// Keying the document by `dedupeKey` makes generators idempotent: re-running a
// generator upserts the same row in place rather than piling up duplicates.
// The presentation fields mirror the webapp `NudgeView` vocabulary so the
// existing insight-card renderer can display them unchanged; `icon` is a lucide
// icon *name* (string) resolved to a component client-side via an allowlist.
// ---------------------------------------------------------------------------

export const InsightScope = z.enum(["business", "team", "member"]);
export type InsightScope = z.infer<typeof InsightScope>;

export const InsightSource = z.enum(["rule", "ai"]);
export type InsightSource = z.infer<typeof InsightSource>;

export const InsightStatus = z.enum([
  "active",
  "dismissed",
  "snoozed",
  "expired",
]);
export type InsightStatus = z.infer<typeof InsightStatus>;

/** Matches the webapp `NudgeSeverity`. */
export const InsightSeverity = z.enum(["info", "warning", "danger"]);
export type InsightSeverity = z.infer<typeof InsightSeverity>;

/** Matches the webapp `NudgeAccent`. */
export const InsightAccent = z.enum(["primary", "warning", "danger", "info"]);
export type InsightAccent = z.infer<typeof InsightAccent>;

export const InsightCta = z.object({
  label: z.string().min(1),
  to: z.string().min(1),
});
export type InsightCta = z.infer<typeof InsightCta>;

export const InsightCtaVariant = z.enum(["link", "button"]);
export type InsightCtaVariant = z.infer<typeof InsightCtaVariant>;

/**
 * Who may see the insight. Distinct from `scope`: member-scoped pulse insights
 * are intentionally manager-visible, while `"owner"` visibility hides an
 * insight from everyone except `ownerUid` (used for insights derived from a
 * user's personally connected accounts, e.g. their calendar).
 */
export const InsightVisibility = z.enum(["business", "owner"]);
export type InsightVisibility = z.infer<typeof InsightVisibility>;

/**
 * A vault document an insight was grounded in. The title is denormalised so a
 * card can attribute its source without a second read, and still renders if the
 * document was later deleted; the id is kept so the UI can link to it.
 */
export const InsightSourceDocument = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
});
export type InsightSourceDocument = z.infer<typeof InsightSourceDocument>;

/** Vault documents attributed on a single insight card. */
export const MAX_INSIGHT_SOURCE_DOCUMENTS = 3;

/**
 * Model-derived event timestamp: kept only when it parses as a real date
 * (finite `Date.parse`), otherwise dropped to null instead of rejecting - a
 * malformed value must not poison timeline sorting or the gather cursor.
 *
 * Normalised to UTC ISO on the way in (GRA-47). The model writes whatever the
 * provider gave it - `2026-08-20T10:00:00+02:00`, a bare `2026-08-20`, a
 * locale string - and several consumers compare these as plain strings
 * (`nextCursor`, `mapMemberCallDrafts`) while others compare them as instants
 * (the member timeline). Only one canonical shape makes those two agree:
 * `2026-08-20T10:00:00+02:00` sorts *after* `2026-08-20T09:00:00.000Z`
 * lexicographically while being an hour earlier, which is enough to walk the
 * gather watermark past calls that were never gathered.
 */
export const ModelDerivedTimestamp = z
  .string()
  .nullable()
  .default(null)
  .transform((v) => {
    if (v == null) return null;
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
  });

/**
 * An external record (e.g. a recorded sales call) an insight was derived from.
 * Denormalised like `InsightSourceDocument` so the card renders even if the
 * provider record disappears; `url` lets the UI deep-link into the provider.
 */
export const InsightSourceRecord = z.object({
  /** Uppercase Composio toolkit slug, e.g. "CUSTOM_GARBA_AI". */
  toolkit: z.string().min(1),
  /** The provider's own id for the record (call id etc.). */
  externalId: z.string().min(1),
  url: z.string().nullable().default(null),
  title: z
    .string()
    .min(1)
    .transform((t) => t.slice(0, 120)),
  /** When the underlying event happened (ISO), if the provider exposes it. */
  occurredAt: ModelDerivedTimestamp,
});
export type InsightSourceRecord = z.infer<typeof InsightSourceRecord>;

/** External records attributed on a single insight card. */
export const MAX_INSIGHT_SOURCE_RECORDS = 3;

// ---------------------------------------------------------------------------
// Generator output
//
// What a generator emits per run. The store layer stamps id/businessId,
// lifecycle, and timestamps; generators stay focused on content + provenance.
// ---------------------------------------------------------------------------

export const GeneratedInsight = z.object({
  generatorKey: z.string().min(1),
  /** Stable key per (generator, subject); the Firestore doc id. */
  dedupeKey: z.string().min(1),
  source: InsightSource,
  scope: InsightScope.default("business"),
  salesTeamId: z.string().min(1).nullable().default(null),
  memberId: z.string().min(1).nullable().default(null),
  visibility: InsightVisibility.default("business"),
  /** Required when visibility is "owner": the only uid allowed to see it. */
  ownerUid: z.string().min(1).nullable().default(null),
  /** Composio toolkit slugs the insight was derived from (UI attribution). */
  sourceToolkits: z.array(z.string()).default([]),
  /** Vault documents the insight was grounded in (UI attribution). */
  sourceDocuments: z.array(InsightSourceDocument).default([]),
  /** External records the insight was derived from (timeline deep links). */
  sourceRecords: z
    .array(InsightSourceRecord)
    .default([])
    .transform((a) => a.slice(0, MAX_INSIGHT_SOURCE_RECORDS)),
  /**
   * When the underlying event happened (ISO). Distinct from `createdAt`
   * (generation time); the member timeline anchors on this when present.
   */
  occurredAt: z.string().nullable().default(null),

  // presentation (NudgeView-compatible)
  severity: InsightSeverity,
  accent: InsightAccent,
  icon: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  cta: InsightCta.nullable().default(null),
  ctaVariant: InsightCtaVariant.optional(),
  starterLabel: z.string().max(60).nullable().default(null),
  suggestedPrompt: z.string().nullable().default(null),
  priority: z.number(),

  // provenance + quality
  confidence: z.number().min(0).max(1).nullable().default(null),
  evidence: z.array(z.string()).default([]),
  promptVersion: z.number().int().nonnegative().nullable().default(null),
  modelId: z.string().min(1).nullable().default(null),
  contextFingerprint: z.string().min(1).nullable().default(null),

  // lifecycle hints (the store owns `status`)
  validFrom: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
});
export type GeneratedInsight = z.infer<typeof GeneratedInsight>;

/**
 * Max suggested actions the model may attach to a single insight. Lives here
 * (not action.ts) together with `AiActionDraft` so `action.ts` can depend on
 * this file without a cycle.
 */
export const ACTIONS_MAX_PER_INSIGHT = 2;

/**
 * A concrete action the model proposes alongside an insight ("AI proposes,
 * user accepts"). The generator wraps each draft into a `GeneratedAction`
 * (see action.ts) inheriting scope/subject from the validated parent insight.
 */
export const AiActionDraft = z.object({
  dedupeSuffix: z
    .string()
    .min(1)
    .describe("Short stable slug for this action, e.g. '1on1-anna-workload'"),
  /**
   * The KPI the action is meant to move, validated server-side against the
   * active definitions rendered into the prompt ("[id: ...]"); invalid ids are
   * nulled. Lets the follow-up generator judge the targeted KPI directly.
   */
  expectedKpiDefinitionId: z
    .string()
    .nullable()
    .default(null)
    .describe(
      "Id på den KPI (från KPI-listan, [id: ...]) som åtgärden ska flytta. null när ingen specifik KPI avses.",
    ),
  title: z
    .string()
    .min(1)
    .max(80)
    .describe("Imperativ, konkret: 'Boka 1:1 med Anna om arbetsbelastning'"),
  description: z.string().min(1).max(300),
  expectedEffect: z
    .string()
    .max(200)
    .nullable()
    .default(null)
    .describe(
      "Mätbar förväntad effekt, t.ex. 'Wellbeing-trenden för Anna vänder inom 2 perioder'",
    ),
  priority: z
    .number()
    .min(0)
    .max(100)
    .describe(
      "Relativ vikt inom denna batch, 0–100 - jämför mot batchens övriga poster",
    ),
});
export type AiActionDraft = z.infer<typeof AiActionDraft>;

/**
 * The slice an AI generator is asked to return via `generateObject`. Keeps the
 * model focused on content + reasoning; the generator wraps each item with the
 * deterministic fields (generatorKey, dedupeKey, source, provenance).
 */
export const AiInsightDraft = z.object({
  dedupeSuffix: z
    .string()
    .min(1)
    .describe("Short stable slug for this insight, e.g. 'team-dynamics-risk'"),
  scope: InsightScope.default("business"),
  memberId: z.string().nullable().default(null),
  salesTeamId: z.string().nullable().default(null),
  severity: InsightSeverity,
  accent: InsightAccent,
  icon: z.string().min(1),
  title: z.string().min(1).max(80),
  description: z.string().min(1).max(400),
  starterLabel: z.string().max(40).nullable().default(null),
  suggestedPrompt: z.string().nullable().default(null),
  priority: z
    .number()
    .min(0)
    .max(100)
    .describe(
      "Relativ vikt inom denna batch, 0–100 - jämför mot batchens övriga poster",
    ),
  confidence: z.number().min(0).max(1),
  // Model-output arrays truncate instead of rejecting — a `.max()` here would
  // fail the whole batch over one extra element (see MEMORY_MAX_OPS_PER_RUN).
  evidence: z
    .array(z.string())
    .describe("Max 4 korta punkter")
    .default([])
    .transform((a) => a.slice(0, 4)),
  sourceDocumentTitles: z
    .array(z.string())
    .describe(
      "Titlar på de dokument vars UTDRAG insikten faktiskt bygger på — tom lista när inget dokument användes",
    )
    .default([])
    .transform((a) => a.slice(0, MAX_INSIGHT_SOURCE_DOCUMENTS)),
  suggestedActions: z
    .array(AiActionDraft)
    .describe("Max 2 konkreta åtgärder — bara när insikten motiverar handling")
    .default([])
    .transform((a) => a.slice(0, ACTIONS_MAX_PER_INSIGHT)),
});
export type AiInsightDraft = z.infer<typeof AiInsightDraft>;

export const AiInsightBatch = z.object({
  insights: z
    .array(AiInsightDraft)
    .describe("Max 6 insikter")
    .transform((a) => a.slice(0, 6)),
});
export type AiInsightBatch = z.infer<typeof AiInsightBatch>;

// ---------------------------------------------------------------------------
// Firestore document shape
// ---------------------------------------------------------------------------

export interface InsightRecord {
  id: string;
  businessId: string;
  source: InsightSource;
  generatorKey: string;
  dedupeKey: string;
  scope: InsightScope;
  salesTeamId: string | null;
  memberId: string | null;
  visibility: InsightVisibility;
  ownerUid: string | null;
  sourceToolkits: string[];
  sourceDocuments: InsightSourceDocument[];
  sourceRecords: InsightSourceRecord[];
  occurredAt: string | null;

  severity: InsightSeverity;
  accent: InsightAccent;
  icon: string;
  title: string;
  description: string;
  cta: InsightCta | null;
  ctaVariant?: InsightCtaVariant;
  starterLabel: string | null;
  suggestedPrompt: string | null;
  priority: number;

  confidence: number | null;
  evidence: string[];
  promptVersion: number | null;
  modelId: string | null;
  contextFingerprint: string | null;

  status: InsightStatus;
  validFrom: string | null;
  expiresAt: string | null;
  dismissedBy: string | null;
  /** When the insight was dismissed - windows the efficacy feedback signal. */
  dismissedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const ListInsightsInput = z.object({
  businessId: z.string().min(1),
  scope: InsightScope.optional(),
  /** When set, returns only insights about this team member. */
  memberId: z.string().optional(),
});
export type ListInsightsInput = z.infer<typeof ListInsightsInput>;

export const DismissInsightInput = z.object({
  businessId: z.string().min(1),
  insightId: z.string().min(1),
});
export type DismissInsightInput = z.infer<typeof DismissInsightInput>;

export const SnoozeInsightInput = z.object({
  businessId: z.string().min(1),
  insightId: z.string().min(1),
  until: z.string().min(1),
});
export type SnoozeInsightInput = z.infer<typeof SnoozeInsightInput>;

export const RefreshInsightsInput = z.object({
  businessId: z.string().min(1),
});
export type RefreshInsightsInput = z.infer<typeof RefreshInsightsInput>;

/** Staleness threshold for the on-demand refresh path (ms). */
export const INSIGHTS_STALE_MS = 6 * 60 * 60 * 1000; // 6h
